import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';

import { CONFIGS } from '../../utilities/Configs';
import { ConstructFetchBody, FormatDate, FormatNumber, FormatTime, MoveArrayItem, UrlQueryParam } from '../../utilities/Helpers';
import { TowerPhotoRequest } from '../../utilities/Types';

import { Avatar, Button, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { CheckBox as IconCheckBox, Delete as IconDelete, SwapVert as IconSwapVertical, Warning as IconWarning } from '@material-ui/icons';

import { Loading } from '../../sharedcomponents/Loading';

import { AccountNotesEdit } from './AccountNotesEdit';
import { FaucetEdit } from './FaucetEdit';
import { Photo } from './Photo';
import { RequestNotesEdit } from './RequestNotesEdit';

type Props = {
	towerPhotoRequest: TowerPhotoRequest['Location']['Table'][number],
	onReject: () => void,
	onApprove: () => void,
	onClose: () => void
};

export const RequestManage = (props: Props) => {
	const [towerPhotoRequest, setTowerPhotoRequest] = useState<undefined | TowerPhotoRequest>(undefined);
	const [selectedFaucetPosition, setSelectedFaucetPosition] = useState<undefined | number>(undefined);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const { enqueueSnackbar } = useSnackbar();

	useEffect(() => {
		setTowerPhotoRequest(undefined);

		fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.TOWER_PHOTO_REQUEST_GET, [UrlQueryParam('token'), props.towerPhotoRequest['twrid']])).then(
			(_response) => _response.json()
		).then(
			(_response) => setTowerPhotoRequest(_response['result'])
		).catch(
			() => enqueueSnackbar('An unexpected error has occurred. Please try again later. If problem persists, please contact support.', { variant: 'error' })
		);
	}, [props.towerPhotoRequest]);

	const handleFaucetsSort = (_fromIndex, _toIndex) => {
		if (towerPhotoRequest !== undefined) {
			setTowerPhotoRequest({
				...towerPhotoRequest,
				...{
					'DetectedFaucets': MoveArrayItem(towerPhotoRequest['DetectedFaucets'], _fromIndex, _toIndex)
				}
			});
		}
	};

	const handleFaucetsAdd = () => {
		if (towerPhotoRequest !== undefined) {
			setTowerPhotoRequest({
				...towerPhotoRequest,
				...{
					'DetectedFaucets': [
						...towerPhotoRequest['DetectedFaucets'],
						...[{
							'flid': null,
							'bid': null,
							'brand_name': null,
							'did': null,
							'distributor_name': null,
							'date_requested': null
						}]
					]
				}
			});
		}
	};
	
	const handleFaucetsFill = () => {
		if (towerPhotoRequest !== undefined) {
			setTowerPhotoRequest({
				...towerPhotoRequest,
				...{
					'DetectedFaucets': [...Array(towerPhotoRequest['CurrentFaucets'].length).keys()].map(
						(_i) => (
							towerPhotoRequest['DetectedFaucets'][_i] ||
							{
								'flid': null,
								'bid': null,
								'brand_name': null,
								'did': null,
								'distributor_name': null
							}
						)
					)
				}
			});
		}
	};

	const handleFaucetApprove = (_position, _faucet) => {
		if (towerPhotoRequest !== undefined) {
			setTowerPhotoRequest({
				...towerPhotoRequest,
				...{
					'DetectedFaucets': towerPhotoRequest['DetectedFaucets'].map(
						(_detectedFaucet, _i) => _i === _position ? _faucet : _detectedFaucet
					)
				}
			});
		}
	};

	const handleFaucetReject = (_position) => {
		if (towerPhotoRequest !== undefined) {
			setTowerPhotoRequest({
				...towerPhotoRequest,
				...{
					'DetectedFaucets': towerPhotoRequest['DetectedFaucets'].filter(
						(_detectedFaucet, _i) => _i !== _position
					)
				}
			});
		}
	};

	const getApprovableFaucetsCount = () => {
		return (towerPhotoRequest?.['DetectedFaucets'] || []).reduce(
			(_count, _faucet) => {
				return ((_faucet['bid'] || 0) > 0 && (_faucet['did'] || 0) > 0 ? _count + 1 : _count);
			},
			0
		);
	};

	const handleRequestReject = () => {
		if (towerPhotoRequest !== undefined) {
			setIsSubmitting(true);

			fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.TOWER_PHOTO_REQUEST_DELETE, [UrlQueryParam('token'), props.towerPhotoRequest['twrid']])).then(
				(_response) => _response.json()
			).then(
				(_response) => {
					if (_response?.error !== undefined) {
						enqueueSnackbar(_response?.error.message, { variant: 'error' });
					}
					else {
						props.onReject();
						props.onClose();

						enqueueSnackbar('Request rejected!', { variant: 'success' });
					}
				}
			).catch(
				() => enqueueSnackbar('An unexpected error has occurred. Please try again later. If problem persists, please contact support.', { variant: 'error' })
			);
		}
	};

	const handleRequestApprove = () => {
		if (towerPhotoRequest !== undefined) {
			const post = towerPhotoRequest['DetectedFaucets'].map(
				(_faucet, _i) => ({
					'flID': -1,
					'FaucetID': (towerPhotoRequest['CurrentFaucets'][_i]?.['fid'] || -1),
					'FaucetName': '',
					'BrandID': (_faucet['bid'] || -1),
					'BrandName': _faucet['brand_name'],
					'DistributorID': (_faucet['did'] || -1),
					'DistributorName': _faucet['distributor_name'],
					'OldBrandID': (towerPhotoRequest['CurrentFaucets'][_i]?.['bid'] || -1),
					'OldDistributorID': (towerPhotoRequest['CurrentFaucets'][_i]?.['did'] || -1),
					'OrderNumber': (_i + 1)
				})
			);
			
			setIsSubmitting(true);
			
			fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.TOWER_PHOTO_REQUEST_POST, [UrlQueryParam('token'), props.towerPhotoRequest['twrid'], post])).then(
				(_response) => _response.json()
			).then(
				(_response) => {
					if (_response?.error !== undefined) {
						enqueueSnackbar(_response?.error.message, { variant: 'error' });
					}
					else {
						props.onApprove();
						props.onClose();

						enqueueSnackbar('Request approved!', { variant: 'success' });
					}
				}
			).catch(
				() => enqueueSnackbar('An unexpected error has occurred. Please try again later. If problem persists, please contact support.', { variant: 'error' })
			);
		}
	};

	return (
		<div className="main">
			<div className="row">
				<Avatar src={CONFIGS.STORAGE.IMAGE_LOCATION_URL.replace('{ID}', props.towerPhotoRequest['LocationGuid'])} className="avatar large">{props.towerPhotoRequest['LocationName'].substr(0, 1)}</Avatar>
				<div className="stretch">
					<p className="heading">{props.towerPhotoRequest['LocationName']}</p>
					<p className="mute">{props.towerPhotoRequest['address']}, {props.towerPhotoRequest['citystatezip']}</p>
					{
						towerPhotoRequest !== undefined && (
							<p className="small">
								<a href={CONFIGS.URLS.SYSTEM.replace('{LOCATION_ID}', String(towerPhotoRequest['Location']['Table'][0]['locid'])).replace('{SYSTEM_ID}', String(towerPhotoRequest['Location']['Table'][0]['sysid'])).replace('{STATION_ID}', '').replace('{TOWER_ID}', '')} rel="noreferrer" target="_blank">{towerPhotoRequest['Location']['Table'][0]['SystemName']}</a> {' > '}
								<a href={CONFIGS.URLS.SYSTEM.replace('{LOCATION_ID}', String(towerPhotoRequest['Location']['Table'][0]['locid'])).replace('{SYSTEM_ID}', String(towerPhotoRequest['Location']['Table'][0]['sysid'])).replace('{STATION_ID}', String(towerPhotoRequest['Location']['Table'][0]['stid'])).replace('{TOWER_ID}', '')} rel="noreferrer" target="_blank">{towerPhotoRequest['Location']['Table'][0]['StationName']}</a> {' > '}
								<a href={CONFIGS.URLS.SYSTEM.replace('{LOCATION_ID}', String(towerPhotoRequest['Location']['Table'][0]['locid'])).replace('{SYSTEM_ID}', String(towerPhotoRequest['Location']['Table'][0]['sysid'])).replace('{STATION_ID}', String(towerPhotoRequest['Location']['Table'][0]['stid'])).replace('{TOWER_ID}', String(towerPhotoRequest['Location']['Table'][0]['tid']))} rel="noreferrer" target="_blank">{towerPhotoRequest['Location']['Table'][0]['TowerName']}</a>
							</p>
						)
					}
				</div>
				<div>
					<Button variant="contained" color="default" onClick={props.onClose}>Close</Button>
				</div>
			</div>
			{
				towerPhotoRequest === undefined ? (
					<Loading />
				) : (
					<>
						<Grid container spacing={2}>
							<Grid item xs={12} md={4}>
								<div className="box purple">
									<p className="bold">AI Tower Faucet Request</p>
									<div className="stretch">
										<div className="row">
											<Avatar src={CONFIGS.STORAGE.IMAGE_USER_URL.replace('{ID}', towerPhotoRequest['Location']['Table'][0]['UserGuid'])}>{(towerPhotoRequest['Location']['Table'][0]['CreatorFirstname'] || '').substr(0, 1)}</Avatar>
											<div>
												<p>{towerPhotoRequest['Location']['Table'][0]['CreatorFirstname']} {towerPhotoRequest['Location']['Table'][0]['CreatorLastname']}</p>
												<p className="small mute">{FormatDate(new Date(towerPhotoRequest['Location']['Table'][0]['createdOn']))}, {FormatTime(new Date(towerPhotoRequest['Location']['Table'][0]['createdOn']))}</p>
											</div>
										</div>
									</div>
									<div className="end">
										<Button variant="contained" href={CONFIGS.URLS.TRIP.replace('{ID}', String(towerPhotoRequest['LastTripId']))} target="_blank" className="button white">Trip Details</Button>
									</div>
								</div>
							</Grid>
							<Grid item xs={12} md={8}>
								<Photo
									urlOriginal={CONFIGS.STORAGE.IMAGE_TOWER_URL.ORIGINAL.replace('{ID}', towerPhotoRequest['Location']['Table'][0]['req_guid'])}
									urlEdited={towerPhotoRequest['Location']['Table'][0]['has_cropped'] === true ? CONFIGS.STORAGE.IMAGE_TOWER_URL.EDITED.replace('{ID}', towerPhotoRequest['Location']['Table'][0]['req_guid']) : undefined}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<div className="box orange">
									<p className="bold">Old Values ({FormatNumber(towerPhotoRequest['CurrentFaucets'].length)})</p>
									{
										towerPhotoRequest['CurrentFaucets'].length === 0 ? (
											<p className="mute">No old values found</p>
										) : (
											<div className="column condensed">
												{
													towerPhotoRequest['CurrentFaucets'].map(
														(_faucet, _i) => (
															<div key={_i} className="strip orange">
																<div>
																	{
																		(_faucet['brand_name'] || '').trim() === '' ? (
																			<p className="mute">{FormatNumber(_i + 1)}. No brand</p>
																		) : (
																			<p>{FormatNumber(_i + 1)}. {_faucet['brand_name']}</p>
																		)
																	}
																	{
																		(_faucet['dist_name'] || '').trim() === '' ? (
																			<p className="small mute">No distributor</p>
																		) : (
																			<p className="small">{_faucet['dist_name']}</p>
																		)
																	}
																</div>
															</div>
														)
													)
												}
											</div>
										)
									}
								</div>
							</Grid>
							{
								[2, 4].includes(props.towerPhotoRequest['status']) === true ? (
									<>
										<Grid item xs={12} md={4}>
											<div className="box violet">
												<p className="bold">New Values ({FormatNumber(towerPhotoRequest['DetectedFaucets'].length)})</p>
												{
													towerPhotoRequest['DetectedFaucets'].length === 0 ? (
														<p className="mute">No new values</p>
													) : (
														<div className="column condensed">
															{
																towerPhotoRequest['DetectedFaucets'].map(
																	(_faucet, _i) => (
																		<div key={_i} className="strip orange" style={{ backgroundColor: 'inherit' }}>
																			<div className="row spread">
																				<div>
																					{
																						(_faucet['brand_name'] || '').trim() === '' ? (
																							<p className="mute">{FormatNumber(_i + 1)}. No brand</p>
																						) : (
																							<p>{FormatNumber(_i + 1)}. {_faucet['brand_name']}</p>
																						)
																					}
																					{
																						(_faucet['distributor_name'] || '').trim() === '' ? (
																							<p className="small mute">No distributor</p>
																						) : (
																							<p className="small">{_faucet['distributor_name']}</p>
																						)
																					}
																				</div>
																				<div className="center">
																					{
																						((_faucet['bid'] || 0) > 0 && (_faucet['did'] || 0) > 0) ? (
																							<IconCheckBox fontSize="small" />
																						) : (
																							<IconWarning fontSize="small" className="mute" />
																						)
																					}
																				</div>
																			</div>
																		</div>
																	)
																)
															}
														</div>
													)
												}
											</div>
										</Grid>
										<Grid item xs={12} md={4}>
											{
												props.towerPhotoRequest['status'] === 2 ? (
													<Alert severity="success">
														This request has been approved.
													</Alert>
												) :
												props.towerPhotoRequest['status'] === 4 && (
													<Alert severity="error">
														This request has been rejected.
													</Alert>
												)
											}
										</Grid>
									</>
								) : (
									<>
										<Grid item xs={12} md={4}>
											<div className="box violet">
												<p className="bold">New Values ({FormatNumber(towerPhotoRequest['DetectedFaucets'].length)})</p>
												{
													towerPhotoRequest['DetectedFaucets'].length === 0 ? (
														<p className="mute">No new values</p>
													) : (
														<SortableList lockAxis="y" draggedItemClassName="sortable-list-dragged-item" className="column condensed" onSortEnd={handleFaucetsSort}>
															{
																towerPhotoRequest['DetectedFaucets'].map(
																	(_faucet, _i) => (
																		<SortableItem key={_i}>
																			<div className="strip orange" style={{ backgroundColor: 'inherit' }}>
																				<div className="row spread">
																					<div className="stretch pointer" onClick={() => setSelectedFaucetPosition(_i)}>
																						{
																							(_faucet['brand_name'] || '').trim() === '' ? (
																								<p className="mute">{FormatNumber(_i + 1)}. No brand</p>
																							) : (
																								<p>{FormatNumber(_i + 1)}. {_faucet['brand_name']}</p>
																							)
																						}
																						{
																							(_faucet['distributor_name'] || '').trim() === '' ? (
																								<p className="small mute">No distributor</p>
																							) : (
																								<p className="small">{_faucet['distributor_name']}</p>
																							)
																						}
																					</div>
																					<div className="row center">
																						<SortableKnob>
																							<IconSwapVertical fontSize="small" className="move" />
																						</SortableKnob>
																						{
																							((_faucet['bid'] || 0) > 0 && (_faucet['did'] || 0) > 0) ? (
																								<IconCheckBox fontSize="small" />
																							) : (
																								<IconWarning fontSize="small" className="mute" />
																							)
																						}
																						<IconDelete fontSize="small" className="error pointer" onClick={() => handleFaucetReject(_i)} />
																					</div>
																				</div>
																			</div>
																		</SortableItem>
																	)
																)
															}
														</SortableList>
													)
												}
												<div className="row">
													<Button variant="contained" className="orange" onClick={handleFaucetsAdd}>Add</Button>
													{
														towerPhotoRequest['CurrentFaucets'].length > towerPhotoRequest['DetectedFaucets'].length && (
															<Button variant="contained" className="orange" onClick={handleFaucetsFill}>Fill</Button>
														)
													}
												</div>
											</div>
										</Grid>
										<Grid item xs={12} md={4}>
											<div className="column">
												<Alert severity="info">
													Click ACCEPT to save changes.
												</Alert>
												<div className={'box ' + (towerPhotoRequest['DetectedFaucets'].length > 0 && getApprovableFaucetsCount() === towerPhotoRequest['DetectedFaucets'].length ? 'green' : 'gray')}>
													{
														towerPhotoRequest['DetectedFaucets'].length === 0 ? (
															<p>No new faucets added.</p>
														) :
														getApprovableFaucetsCount() === towerPhotoRequest['DetectedFaucets'].length ? (
															<p>All faucets have been reviewed.</p>
														) : (
															<p>Only <span className="bold">{FormatNumber(getApprovableFaucetsCount())}</span> out of <span className="bold">{FormatNumber(towerPhotoRequest['DetectedFaucets'].length)}</span> faucets have been reviewed.</p>
														)
													}
													<div className="row spread">
														<Button variant="contained" disabled={isSubmitting} className="red" onClick={handleRequestReject}>Reject</Button>
														<Button variant="contained" disabled={(towerPhotoRequest['DetectedFaucets'].length === 0 || isSubmitting)} className="green" onClick={handleRequestApprove}>Accept</Button>
													</div>
												</div>
											</div>
										</Grid>
									</>
								)
							}
							<Grid item xs={12} md={4}>
								<div className="column">
									<p className="title bold">Location Information</p>
									<div>
										<p className="small mute">Zone</p>
										{
											(towerPhotoRequest['Location']['Table'][0]['Zone'] || '').trim() === '' ? (
												<p className="mute">N/A</p>
											) : (
												<p>{towerPhotoRequest['Location']['Table'][0]['Zone']}</p>
											)
										}
									</div>
									<div>
										<p className="small mute">Area</p>
										{
											(towerPhotoRequest['Location']['Table'][0]['Area'] || '').trim() === '' ? (
												<p className="mute">N/A</p>
											) : (
												<p>{towerPhotoRequest['Location']['Table'][0]['Area']}</p>
											)
										}
									</div>
									<div>
										<p className="small mute">Exception Account</p>
										{
											(towerPhotoRequest['Location']['Table'][0]['IsExceptionAccount'] || '').trim() === '' ? (
												<p className="mute">N/A</p>
											) : (
												<p>{towerPhotoRequest['Location']['Table'][0]['IsExceptionAccount']}</p>
											)
										}
									</div>
								</div>
							</Grid>
							<Grid item xs={12} md={4}>
								<RequestNotesEdit towerPhotoRequest={towerPhotoRequest} />
							</Grid>
							<Grid item xs={12} md={4}>
								<AccountNotesEdit towerPhotoRequest={towerPhotoRequest} />
							</Grid>
						</Grid>
						{
							selectedFaucetPosition !== undefined && (
								<FaucetEdit towerPhotoRequest={towerPhotoRequest} position={selectedFaucetPosition} onApprove={handleFaucetApprove} onReject={handleFaucetReject} onClose={() => setSelectedFaucetPosition(undefined)} />
							)
						}
					</>
				)
			}
		</div>
	);
};